<!-- 
Risk factor table for display on a levy collection sandbox.

We have a separate component for this because when used on a sandbox 
we want it to show changes from the original settings.
-->
<template>
  <v-card flat class="mr-2">
    <v-card-title primary-title> 
      <h3 class="title mb-0">
        {{ title }}
      </h3>
    </v-card-title>
    <v-card-text class="pr-2 mr-2 pb-0">
      <v-data-table 
        :headers="headers" 
        :items="combinedFactors" 
        :total-items="combinedFactors.length" 
        :loading="state.loading" 
        hide-actions 
        class="risk-factor-table"
      >
        <template slot="items" slot-scope="props">
          <tr class="noselect">
            <td :class="{deleted: props.item.deleted, new: props.item.new}">{{ props.item.description }}</td>
            <td :class="{deleted: props.item.deleted, new: props.item.new}">{{ props.item.score | numericInput2 }}</td>
            <td>
              <v-tooltip v-if="!props.item.deleted" left>
                <v-btn 
                  slot="activator" 
                  small 
                  icon 
                  class="ma-0 primary--text" 
                  @click.prevent="edit(props.item)"
                >
                  <v-icon small>edit</v-icon>
                </v-btn>
                <span>Edit this range</span>
              </v-tooltip>
              <v-tooltip v-if="!props.item.deleted" right>
                <v-btn
                  slot="activator" 
                  small
                  icon
                  class="ma-0 error--text" 
                  @click.prevent="del(props.item)"
                >
                  <v-icon small>clear</v-icon>
                </v-btn>
                <span>Delete this range</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-actions class="justify-center">
      <v-btn @click="showCreateDialog=true">
        <v-icon class="mr-2">add</v-icon>
        Add new range
      </v-btn>
    </v-card-actions>
    <risk-factor-edit-dialog 
      v-model="showEditDialog" 
      :levy-collection-id="levyCollectionId" 
      :risk-factor-id="editRiskFactorId"
      :risk-factor-type="type" 
      :lower-bound-label="tableTitle ? tableTitle : title"
      :terminology="terminology"
      for-sandbox
    />
    <risk-factor-edit-dialog 
      v-model="showCreateDialog" 
      :levy-collection-id="levyCollectionId" 
      :risk-factor-type="type" 
      :lower-bound-label="tableTitle ? tableTitle : title"
      :terminology="terminology"
      for-sandbox
    />
    <risk-factor-delete-dialog
      v-model="showDeleteDialog"
      :levy-collection-id="levyCollectionId"
      :risk-factor-id="deleteRiskFactorId"
    />
  </v-card>
</template>

<script>
import RiskFactorEditDialog from "@/components/levy-collection-risk-factors/RiskFactorEditDialog.vue";
import RiskFactorDeleteDialog from "@/components/levy-collection-risk-factors/RiskFactorDeleteDialog.vue";
import { LEVY_COLLECTION_SANDBOXES } from "@/store/claim-types";
import * as action from "@/store/levy-collection-risk-factors/action-types";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as formatting from "@/helpers/formatting.helper";
import _ from "underscore";

export default {
  components: { RiskFactorEditDialog, RiskFactorDeleteDialog },
  mixins: [filterMixins, lookupMixins],
  props: {
    type: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    terminology: {
      type: String,
      default: "Value"
    },
    tableTitle: {
      type: String,
      default: null
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    },
    isCurrency: {
      type: Boolean,
      default: false
    },
    isPercentage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      headers: [
        { text: this.tableTitle ? this.tableTitle : this.title, align: "left", sortable: false, value: "range" },
        { text: this.terminology, align: "left", sortable: false, value: "score" },
        { text: "", value: "Id", sortable: false, align: "right", width: 110 }
      ],
      showEditDialog: false,
      showCreateDialog: false,
      showDeleteDialog: false,
      editRiskFactorId: null,
      deleteRiskFactorId: null
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionRiskFactors.listing;
    },
    originalState() {
      return this.$store.state.sandboxes.original.riskFactors;
    },
    loaded() {
      return this.state.loaded;
    },
    riskFactorType() {
      return this.getLookup(this.type, this.lookups.riskFactorTypes);
    },
    currentFactors() {
      var factors = _(this.state.data).filter(r => r.type == this.type);
      return _(factors).map(f => ({
        id: f.id,
        lowerBound: f.lowerBound,
        description: formatting.getRiskFactorLowerBoundDescription(f, factors, this.riskFactorType),
        score: f.score
      }));
    },
    originalFactors() {
      var factors = _(this.originalState).filter(r => r.type == this.type);
      return _(factors).map(f => ({
        lowerBound: f.lowerBound,
        description: formatting.getRiskFactorLowerBoundDescription(f, factors, this.riskFactorType),
        score: f.score
      }));
    },
    combinedFactors() {
      const current = _(this.currentFactors).map(f => ({
        id: f.id,
        lowerBound: f.lowerBound,
        description: f.description,
        score: f.score,
        new: !_(this.originalFactors).some(o => o.description == f.description && o.score == f.score),
        deleted: false
      }));
      const originalDiffs = _(this.originalFactors).filter(
        f => !_(this.currentFactors).some(o => o.description == f.description && o.score == f.score)
      );
      const original = _(originalDiffs).map(f => ({
        id: null,
        lowerBound: f.lowerBound,
        description: f.description,
        score: f.score,
        new: false,
        deleted: true
      }));
      return _(_.union(current, original)).sortBy(f => -f.lowerBound);
    },
    canEdit() {
      return this.$store.getters.claims.includes(LEVY_COLLECTION_SANDBOXES);
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      if (this.levyCollectionId) {
        const isDifferentLevyCollection = this.state.levyCollectionId != this.levyCollectionId;
        if ((!this.state.loaded && !this.state.loading) || isDifferentLevyCollection) {
          this.$store.dispatch(action.VIEW, this.levyCollectionId);
        }
      }
    },
    edit(riskFactor) {
      this.editRiskFactorId = riskFactor.id;
      this.showEditDialog = true;
    },
    del(riskFactor) {
      this.deleteRiskFactorId = riskFactor.id;
      this.showDeleteDialog = true;
    }
  }
};
</script>

<style lang="scss">
.risk-factor-table table {
  border: 1px solid #f0f0f0;
  td.deleted {
    text-decoration: line-through;
  }
  td.new {
    font-weight: bold !important;
  }
}
</style>